// ViewMeetingModal.js
import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import Multiselect from "multiselect-react-dropdown";
import CreatableSelect from "react-select/creatable";
import { Calendar, Clock, FileText, Tag, Hash, Trash, XCircle } from "react-bootstrap-icons";
import { deleteLiveClassAPI, updateDetailsLiveClassAPI } from "ServerAPI/serverAPI";
import { Users } from "lucide-react";

const ViewMeetingModal = ({
  show,
  onHide,
  liveClass,
  userGroupUsers, // All users in the group for the dropdown
  cookies,
  showToast,
  onMeetingUpdated, // Callback to refresh the list after update/delete
}) => {
  // --- STATE MANAGEMENT ---
  const [meetingData, setMeetingData] = useState(null);
  const [initialMeetingData, setInitialMeetingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // --- UTILITY FUNCTIONS ---
  // Converts DD-MM-YYYY to YYYY-MM-DD for the date input
  const formatToInputDate = (dateString) => {
    if (!dateString || dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return "";
  };

  // Converts YYYY-MM-DD from input to DD-MM-YYYY for the API
  const formatToApiDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  // --- EFFECT HOOKS ---
  // Initialize or reset the form state when the modal is shown or the class data changes
  useEffect(() => {
    if (show && liveClass) {
      const formattedData = {
        title: liveClass.title || "",
        description: liveClass.description || "",
        date: liveClass.date,
        startTime: liveClass.startTime || "",
        endTime: liveClass.endTime || "",
        participants: liveClass.participants || [],
        maxParticipants: liveClass.maxParticipants || 0,
        tags: (liveClass.tags || []).map(tag => ({ label: tag, value: tag })),
        timezone: liveClass.timezone || "IST",
        // status: liveClass.status || "scheduled", // WIP
        // isRecurring: liveClass.isRecurring || false, // WIP
        // recurringPattern: liveClass.recurringPattern || "weekly", // WIP
      };
      setMeetingData(formattedData);
      setInitialMeetingData(formattedData); // Keep a copy for change detection
      setShowDeleteConfirm(false); // Reset delete confirmation view
      setFormErrors({});
    }
  }, [show, liveClass]);

  // --- FORM HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setMeetingData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleParticipantsChange = (selectedOptions) => {
    const participantIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setMeetingData(prev => ({ ...prev, participants: participantIds }));
  };

  const handleTagsChange = (newValue) => {
    setMeetingData(prev => ({ ...prev, tags: newValue || [] }));
  };

  // --- CORE LOGIC: UPDATE & DELETE ---
  const handleUpdate = async () => {
    setIsLoading(true);
    const apiCalls = [];

    console.log("🔍 Initial Participants:", initialMeetingData.participants);
    console.log("📝 Current Participants:", meetingData.participants);

    // 1. Detect Participant Changes
    const initialParticipants = new Set(initialMeetingData.participants);
    const currentParticipants = new Set(meetingData.participants);
    const usersToAdd = meetingData.participants.filter(p => !initialParticipants.has(p));
    const usersToRemove = initialMeetingData.participants.filter(p => !currentParticipants.has(p));

    // Single API call for participant updates if there are changes
    if (usersToAdd.length > 0 || usersToRemove.length > 0) {
      const participantPayload = {
        operation: "updateParticipants",
        liveClassId: liveClass.live_class_id,
      };

      // Only include 'add' field if there are users to add
      if (usersToAdd.length > 0) {
        participantPayload.add = usersToAdd;
      }

      // Only include 'remove' field if there are users to remove
      if (usersToRemove.length > 0) {
        participantPayload.remove = usersToRemove;
      }

      console.log("📤 Sending updateParticipants payload:", participantPayload);
      apiCalls.push(() => updateDetailsLiveClassAPI(participantPayload));
    }

    // 2. Detect Detail Changes
    const detailsPayload = {
      operation: "updateDetails",
      liveClassId: liveClass.live_class_id,
    };

    const fieldsToCompare = ['title', 'description', 'startTime', 'endTime', 'maxParticipants'];
    fieldsToCompare.forEach(field => {
      if (meetingData[field] !== initialMeetingData[field]) {
        // Ensure maxParticipants is an integer
        detailsPayload[field] = field === 'maxParticipants'
          ? parseInt(meetingData[field], 10)
          : meetingData[field];
      }
    });

    if (formatToApiDate(meetingData.date) !== formatToApiDate(initialMeetingData.date)) {
      detailsPayload.date = formatToApiDate(meetingData.date);
    }

    const initialTags = new Set(initialMeetingData.tags.map(t => t.value));
    const currentTags = new Set(meetingData.tags.map(t => t.value));
    if (initialTags.size !== currentTags.size || ![...initialTags].every(t => currentTags.has(t))) {
      detailsPayload.tags = meetingData.tags.map(t => t.value);
    }

    if (Object.keys(detailsPayload).length > 2) {
      console.log("📤 Sending updateDetails payload:", detailsPayload);
      apiCalls.push(() => updateDetailsLiveClassAPI(detailsPayload));
    }

    // 3. Execute API calls sequentially
    if (apiCalls.length === 0) {
      console.log("⚠️ No changes detected, skipping API calls.");
      showToast("No changes detected.", "info");
      setIsLoading(false);
      return;
    }

    console.log("🚀 Number of API calls to make:", apiCalls.length);

    try {
      for (let i = 0; i < apiCalls.length; i++) {
        console.log(`🔄 Executing API call ${i + 1} of ${apiCalls.length}`);
        const response = await apiCalls[i]();
        if (response.status !== 200) {
          console.log(`❌ API call ${i + 1} failed with status:`, response.status);
          showToast("An update operation failed.", "error");
        } else {
          console.log(`✅ API call ${i + 1} succeeded.`);
        }
      }

      showToast("Live class updated successfully!", "success");
      onMeetingUpdated();
      onHide();
    } catch (error) {
      console.error("❗ Error updating live class:", error);
      showToast(error?.response?.data?.message || "Failed to update live class.", "error");
    } finally {
      setIsLoading(false);
    }
  };


  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const payload = {
        liveClassId: liveClass.live_class_id,
        userGroupId: liveClass.userGroupId,
        // requester_user_id: cookies.userId, // WIP
        // deletion_reason: "Class cancelled by user.", // WIP
        // keep_audit_log: true, // WIP
        // delete_all_recurring: false, // WIP
      };
      console.log("payload", payload);
      const response = await deleteLiveClassAPI(payload);
      if (response.status === 200) {
        showToast("Live class deleted successfully.", "success");
        onMeetingUpdated();
        onHide();
      } else {
        showToast("Failed to delete.", "error");
      }
    } catch (error) {
      console.error("Error deleting live class:", error);
      showToast(error?.response?.data?.message || "Failed to delete live class.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // --- RENDER ---
  if (!meetingData) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Body><Spinner animation="border" /></Modal.Body>
      </Modal>
    );
  }

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      fontSize: "14px",
      padding: "4px 8px",
      borderRadius: "8px",
      borderColor: state.isInvalid ? "#dc3545" : (state.isFocused ? "#224D99" : "#ced4da"),
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(34, 77, 153, 0.25)" : "none",
      "&:hover": { borderColor: state.isInvalid ? "#dc3545" : "#224D99" }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#224D9920",
      borderRadius: "6px"
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#224D99",
      fontSize: "13px"
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#224D99",
      "&:hover": { backgroundColor: "#224D99", color: "white" }
    })
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title style={{ color: "#224D99", fontSize: "24px", fontWeight: 600 }}>
          Class Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {showDeleteConfirm ? (
          <div className="text-center p-4">
            <XCircle size={48} className="text-danger mb-3" />
            <h4>Are you sure?</h4>
            <p>This action will permanently delete the live class. This cannot be undone.</p>
          </div>
        ) : (
          <Form>
            {/* Form fields are laid out identically to CreateMeetingModal */}
            {/* Title */}
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center mb-2"><FileText size={16} className="me-2 text-primary" /><strong>Class Title</strong></Form.Label>
                  <Form.Control type="text" name="title" value={meetingData.title} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            {/* Description */}
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center mb-2"><FileText size={16} className="me-2 text-muted" /><strong>Description</strong></Form.Label>
                  <Form.Control as="textarea" rows={3} name="description" value={meetingData.description} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            {/* Date and Time */}
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label><Calendar size={16} className="me-2 text-success" /><strong>Date</strong></Form.Label><Form.Control type="date" name="date" value={meetingData.date} onChange={handleChange} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label><Clock size={16} className="me-2 text-warning" /><strong>Start Time</strong></Form.Label><Form.Control type="time" name="startTime" value={meetingData.startTime} onChange={handleChange} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label><Clock size={16} className="me-2 text-danger" /><strong>End Time</strong></Form.Label><Form.Control type="time" name="endTime" value={meetingData.endTime} onChange={handleChange} /></Form.Group></Col>
            </Row>

            {/* Participants and Capacity */}
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center mb-2"><Users size={16} className="me-2 text-info" /><strong>Participants</strong></Form.Label>
<Multiselect
  options={userGroupUsers}
  selectedValues={userGroupUsers.filter(u => meetingData.participants.includes(u.value))}
  onSelect={selectedList => handleParticipantsChange(selectedList)}
  onRemove={selectedList => handleParticipantsChange(selectedList)}
  displayValue="label"
  placeholder="Select participants"
  closeOnSelect={false}
  showCheckbox={true}
  style={{
    searchBox: {
      borderColor: '#ccd6e0',
      borderRadius: '6px',
      padding: '8px',
      minHeight: '40px',
    },
    chips: {
      backgroundColor: '#85cbfdff',
      color: 'black',
      fontWeight: '500',
    },
    optionContainer: {
      maxHeight: '180px',
      overflowY: 'auto',
    },
  }}
/>



                </Form.Group>
              </Col>
              {/* <Col md={8}>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center mb-2"><Users size={16} className="me-2 text-info" /><strong>Participants</strong></Form.Label>
                  <Select
                    options={userGroupUsers.map(u => ({ value: u.value, label: u.label }))}
                    isMulti
                    value={userGroupUsers.filter(u => meetingData.participants.includes(u.value)).map(u => ({ value: u.value, label: u.label }))}
                    onChange={handleParticipantsChange}
                    styles={selectStyles}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center mb-2"><Hash size={16} className="me-2 text-muted" /><strong>Max Participants</strong></Form.Label>
                  <Form.Control type="number" name="maxParticipants" value={meetingData.maxParticipants} onChange={handleChange} min="1" />
                </Form.Group>
              </Col> */}
            </Row>

            {/* Tags */}
            {/* <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Form.Label className="d-flex align-items-center mb-2"><Tag size={16} className="me-2 text-secondary" /><strong>Tags</strong></Form.Label>
                  <CreatableSelect isMulti onChange={handleTagsChange} value={meetingData.tags} styles={selectStyles} />
                </Form.Group>
              </Col>
            </Row> */}
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 d-flex">
        {showDeleteConfirm ? (
          <>
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={isLoading}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} disabled={isLoading}>
              {isLoading ? <Spinner as="span" animation="border" size="sm" /> : <><Trash /> Confirm Delete</>}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-danger" onClick={() => setShowDeleteConfirm(true)} disabled={isLoading} className="me-auto">
              <Trash /> Delete
            </Button>
            <Button variant="secondary" onClick={onHide} disabled={isLoading}>Cancel</Button>
            <Button variant="primary" onClick={handleUpdate} disabled={isLoading} style={{ minWidth: '160px', backgroundColor: "#224D99", borderColor: "#224D99" }}>
              {isLoading ? (
                <><Spinner as="span" animation="border" size="sm" className="me-2" />Updating...</>
              ) : "Update Changes"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ViewMeetingModal;
